import { useEffect, useRef, useState } from 'react';
import { Hash, Lock, Users, X } from 'lucide-react';
import type { AvailableSource, IntegrationSource } from '../../lib/api';
import { Input } from '../ui/input';

/**
 * Choosing which Slack channels an integration watches. Declarative: `onChange` gets the complete
 * set, so removing a channel is the same call as adding one.
 */
export function SourcePicker({
  sources,
  available,
  busy,
  onChange,
}: {
  sources: IntegrationSource[];
  available: AvailableSource[];
  busy: boolean;
  onChange: (externalIds: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const chosen = new Set(sources.map((s) => s.externalId));
  const matches = available.filter(
    (s) => !chosen.has(s.externalId) && (s.name ?? s.externalId).toLowerCase().includes(search.toLowerCase()),
  );

  const add = (externalId: string) => {
    if (chosen.has(externalId)) return;
    onChange([...chosen, externalId]);
    setSearch('');
    setOpen(false);
  };
  const remove = (externalId: string) => onChange([...chosen].filter((id) => id !== externalId));

  return (
    <>
      <div className="relative" ref={boxRef}>
        <Input
          placeholder="Search a channel the bot is in, or paste a channel ID…"
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (matches.length === 1) add(matches[0].externalId);
            // A private channel the bot can see but conversations.list does not return.
            else if (search.trim()) add(search.trim());
          }}
          disabled={busy}
        />
        {open && (
          <div className="absolute left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg py-1 z-10 max-h-56 overflow-y-auto">
            {matches.length === 0 ? (
              <p className="px-3 py-2 text-xs text-ink-faint">
                {available.length === 0 ? 'No channels to suggest — paste a channel ID directly' : 'No matching channel'}
              </p>
            ) : (
              matches.map((s) => (
                <button
                  key={s.externalId}
                  onClick={() => add(s.externalId)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-subtle transition-colors"
                >
                  {s.isPrivate ? (
                    <Lock size={12} className="text-ink-faint flex-shrink-0" />
                  ) : (
                    <Hash size={12} className="text-ink-faint flex-shrink-0" />
                  )}
                  <span className="text-sm text-ink-secondary truncate flex-1">{s.name ?? s.externalId}</span>
                  {s.detail && <span className="text-xs text-ink-faint flex-shrink-0">{s.detail}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {sources.length === 0 ? (
        <p className="text-sm text-ink-faint py-4 text-center">No channels watched yet.</p>
      ) : (
        <ul className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
          {sources.map((s) => {
            const detail = available.find((a) => a.externalId === s.externalId);
            return (
              <li key={s.externalId} className="flex items-center gap-3 px-3 py-2.5">
                {detail?.isPrivate ? (
                  <Lock size={13} className="text-orange-400 flex-shrink-0" />
                ) : (
                  <Hash size={13} className="text-orange-400 flex-shrink-0" />
                )}
                <span className="text-sm truncate flex-1 text-ink-secondary">{s.name ?? detail?.name ?? s.externalId}</span>
                {detail?.detail && (
                  <span className="flex items-center gap-1 text-xs text-ink-faint flex-shrink-0">
                    <Users size={12} />
                    {detail.detail.replace(' members', '')}
                  </span>
                )}
                <button
                  onClick={() => remove(s.externalId)}
                  disabled={busy}
                  className="text-ink-faint hover:text-red-500 transition-colors flex-shrink-0 disabled:opacity-40"
                >
                  <X size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
